import { ComponentProps, useState } from 'react';
// Hooks
import { useBreakpoints } from 'hooks';
// UI
import Select from 'ui3/Select/Select';
import MobileSelectSheet, { SheetOption } from './MobileSelectSheet';
import MobileSelectTrigger from './MobileSelectTrigger';

type SelectWithMobileSheetProps = {
  name?: string;
  label?: string;
  placeholder?: string;
  value?: SheetOption | null;
  options: SheetOption[];
  onChange: (option: SheetOption) => void;
  getOptionLabel?: (option: SheetOption) => string;
  getOptionValue?: (option: SheetOption) => string;
  error?: string;
} & Pick<ComponentProps<typeof Select>, 'disabled'>;

const defaultGetLabel = (option: SheetOption) => String(option.label);
const defaultGetValue = (option: SheetOption) => String(option.value);

/**
 * Same as Select on desktop. Below the desktop breakpoint it shows a trigger that opens
 * a full screen list, so options are never hidden behind the on-screen keyboard.
 */
const SelectWithMobileSheet = ({
  name = 'select',
  label,
  placeholder = 'Select',
  value,
  options,
  onChange,
  getOptionLabel = defaultGetLabel,
  getOptionValue = defaultGetValue,
  error,
  disabled,
}: SelectWithMobileSheetProps) => {
  const { isBreakpointL } = useBreakpoints();
  const [open, setOpen] = useState(false);

  if (isBreakpointL) {
    return (
      <Select
        label={label}
        placeholder={placeholder}
        value={value}
        options={options}
        onChange={onChange}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        error={error}
        disabled={disabled}
      />
    );
  }

  return (
    <>
      <MobileSelectTrigger
        name={name}
        label={label}
        placeholder={placeholder}
        valueText={value ? getOptionLabel(value) : undefined}
        error={error}
        disabled={disabled}
        onClick={() => setOpen(true)}
      />
      <MobileSelectSheet
        open={open}
        title={label || placeholder}
        options={options}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        selectedValue={value ? getOptionValue(value) : null}
        onSelect={(option) => {
          onChange(option);
          setOpen(false);
        }}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

export default SelectWithMobileSheet;
